import {
	ChatAgentOption,
	ChatAttachment,
	ChatDeltaResult,
	ChatInstructResult,
	ChatMentionSuggestion,
	ChatMessage,
	ChatMessagePage,
	ChatParticipant,
	ChatSessionResult,
	ChatSessionSearchResult,
	PromptChatAdapter,
} from './PromptChatAdapter';

type DesignerChatSessionModel = {
	sessionId: string;
	isNew: boolean;
	name?: string;
	createdDate: string;
	expiresAt?: string;
	status?: string;
	agentId?: string;
	agentName?: string;
	messages?: ChatMessage[];
	hasMoreMessages?: boolean;
	oldestMessageSequence?: number;
};

type DesignerChatSessionSearchResult = {
	items: DesignerChatSessionModel[];
	hasNextPage: boolean;
};

type DesignerChatDeltaResult = {
	status?: string;
	isComplete: boolean;
	lastMessageSequence?: number;
	hasMoreUpdates?: boolean;
	changeToken?: string;
	responseStatus?: string;
	responseTurnId?: string;
	responseUpdatedAt?: string;
	messages: ChatMessage[];
	hasMoreMessages?: boolean;
	oldestMessageSequence?: number;
};

type DesignerChatMessagePageResult = {
	messages: ChatMessage[];
	hasMoreMessages: boolean;
	oldestMessageSequence?: number;
};

type DesignerChatSessionClient = {
	createOrGetSession(targetId: string, body: { forceNew: boolean; agentId?: string }): Promise<DesignerChatSessionModel>;
	getSessions(targetId: string, pageIndex: number | undefined, pageSize: number | undefined): Promise<DesignerChatSessionSearchResult>;
	updateSession(targetId: string, sessionId: string, body: { name?: string; expiresAt?: string; clearExpiration: boolean }): Promise<void>;
	deleteSession(targetId: string, sessionId: string): Promise<void>;
	addParticipants(targetId: string, sessionId: string, userIds: string[]): Promise<void>;
	getParticipants(targetId: string, sessionId: string): Promise<ChatParticipant[]>;
	removeParticipant(targetId: string, sessionId: string, userId: string): Promise<void>;
	getMessagesDelta(
		targetId: string,
		sessionId: string,
		afterMessageId: string | undefined,
		afterSequence?: number,
		changeToken?: string,
		signal?: AbortSignal,
	): Promise<DesignerChatDeltaResult>;
	getMessagesBefore(targetId: string, sessionId: string, beforeSequence: number, pageSize: number): Promise<DesignerChatMessagePageResult>;
	uploadAttachment?(file: File, conversationId: string, attributes?: Record<string, string>): Promise<ChatAttachment>;
	deleteDraftAttachment?(fileId: string): Promise<void>;
	getAttachmentDownloadUrl?(sessionId: string, fileId: string): Promise<string>;
	getAttachmentHtml?(sessionId: string, fileId: string, signal?: AbortSignal): Promise<string>;
};

type CreateDesignerChatAdapterOptions = {
	getContextAttributes?: PromptChatAdapter['getContextAttributes'];
	agentId?: string;
	cancelResponse?: PromptChatAdapter['cancelResponse'];
	targetId?: string;
	sessionKey?: string;
	isAvailable?: boolean;
	retryableSends?: boolean;
	missingTargetMessage: string;
	sessionClient: DesignerChatSessionClient;
	getAgents?: () => Promise<ChatAgentOption[]>;
	instruct: (
		instruction: string,
		sessionId?: string,
		conversationName?: string,
		forceNew?: boolean,
		agentId?: string,
		messageAttributes?: Record<string, string>,
		fileIds?: string[],
		requestId?: string,
	) => Promise<ChatInstructResult>;
	searchMentions: (query: string) => Promise<ChatMentionSuggestion[]>;
};

function requireTargetId(targetId: string | undefined, message: string): string {
	if (!targetId) {
		throw new Error(message);
	}

	return targetId;
}

export function createDesignerChatAdapter({
	getContextAttributes,
	agentId: fixedAgentId,
	targetId,
	sessionKey,
	isAvailable = !!targetId,
	retryableSends = true,
	missingTargetMessage,
	sessionClient,
	getAgents,
	instruct,
	searchMentions,
	cancelResponse,
}: CreateDesignerChatAdapterOptions): PromptChatAdapter {
	return {
		getContextAttributes,
		agentId: fixedAgentId,
		cancelResponse,
		retryableSends,
		sessionKey: sessionKey ?? targetId ?? missingTargetMessage,
		isAvailable,

		async createOrGetSession(forceNew: boolean, agentId?: string): Promise<ChatSessionResult> {
			const id = requireTargetId(targetId, missingTargetMessage);
			const s = await sessionClient.createOrGetSession(id, { forceNew, agentId });
			return {
				sessionId: s.sessionId,
				isNew: s.isNew,
				name: s.name,
				createdDate: s.createdDate,
				expiresAt: s.expiresAt,
				agentId: s.agentId,
				agentName: s.agentName,
				messages: (s.messages ?? []) as ChatMessage[],
				hasMoreMessages: s.hasMoreMessages,
				oldestMessageSequence: s.oldestMessageSequence,
			};
		},

		async listSessions(pageIndex: number, pageSize: number): Promise<ChatSessionSearchResult> {
			const id = requireTargetId(targetId, missingTargetMessage);
			const result = await sessionClient.getSessions(id, pageIndex, pageSize);
			return {
				items: (result?.items ?? []).map((session) => ({
					sessionId: session.sessionId,
					name: session.name ?? '',
					createdDate: session.createdDate,
					expiresAt: session.expiresAt,
					status: session.status,
					agentId: session.agentId,
					agentName: session.agentName,
				})),
				hasNextPage: result?.hasNextPage ?? false,
			};
		},

		async renameSession(sessionId: string, name: string): Promise<void> {
			const id = requireTargetId(targetId, missingTargetMessage);
			await sessionClient.updateSession(id, sessionId, { name, clearExpiration: false });
		},

		async deleteSession(sessionId: string): Promise<void> {
			const id = requireTargetId(targetId, missingTargetMessage);
			await sessionClient.deleteSession(id, sessionId);
		},

		async addParticipants(sessionId: string, userIds: string[]): Promise<void> {
			const id = requireTargetId(targetId, missingTargetMessage);
			await sessionClient.addParticipants(id, sessionId, userIds);
		},

		async getParticipants(sessionId: string): Promise<ChatParticipant[]> {
			const id = requireTargetId(targetId, missingTargetMessage);
			return sessionClient.getParticipants(id, sessionId);
		},

		async removeParticipant(sessionId: string, userId: string): Promise<void> {
			const id = requireTargetId(targetId, missingTargetMessage);
			await sessionClient.removeParticipant(id, sessionId, userId);
		},

		async getMessagesDelta(
			sessionId: string,
			lastMessageId?: string,
			afterSequence?: number,
			changeToken?: string,
			signal?: AbortSignal,
		): Promise<ChatDeltaResult> {
			const id = requireTargetId(targetId, missingTargetMessage);
			const delta = await sessionClient.getMessagesDelta(id, sessionId, lastMessageId, afterSequence, changeToken, signal);
			return {
				...delta,
				status: delta.status,
				isComplete: delta.isComplete,
				messages: delta.messages as ChatMessage[],
				hasMoreMessages: delta.hasMoreMessages,
				oldestMessageSequence: delta.oldestMessageSequence,
			};
		},

		async getMessagesBefore(sessionId: string, beforeSequence: number, pageSize: number): Promise<ChatMessagePage> {
			const id = requireTargetId(targetId, missingTargetMessage);
			const page = await sessionClient.getMessagesBefore(id, sessionId, beforeSequence, pageSize);
			return {
				messages: page.messages as ChatMessage[],
				hasMoreMessages: page.hasMoreMessages,
				oldestMessageSequence: page.oldestMessageSequence,
			};
		},

		...(sessionClient.uploadAttachment
			? {
					uploadAttachment: async (file: File, conversationId: string, attributes?: Record<string, string>) =>
						sessionClient.uploadAttachment!(file, conversationId, attributes),
				}
			: {}),
		...(sessionClient.deleteDraftAttachment
			? {
					deleteDraftAttachment: async (fileId: string) => sessionClient.deleteDraftAttachment!(fileId),
				}
			: {}),
		...(sessionClient.getAttachmentDownloadUrl
			? {
					getAttachmentDownloadUrl: async (sessionId: string, fileId: string) => sessionClient.getAttachmentDownloadUrl!(sessionId, fileId),
				}
			: {}),

		...(getAgents ? { getAgents } : {}),
		...(sessionClient.getAttachmentHtml ? { getAttachmentHtml: sessionClient.getAttachmentHtml } : {}),
		instruct,
		searchMentions,
	};
}
